import { Download, File, FileImage, FileText, Loader2, Trash2, Upload } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { fmtDate } from '../../shared/format'
import { fmtSize } from '../taskFilters'
import type { Project, ProjectFile } from '../types'
import { toastError, useStaffLookup } from '../ui'
import { downloadFile, useDeleteFile, useUploadFile } from '../workApi'

const iconOf = (mime: string) => (mime.startsWith('image/') ? FileImage : mime === 'application/pdf' || mime.startsWith('text/') ? FileText : File)

/** Dosya yükleme düğmesi; görev detayında ve Dosyalar sekmesinde kullanılır. */
export function UploadButton({ project, taskId, size = 'sm' }: { project: Project; taskId: string | null; size?: 'sm' | 'default' }) {
  const { t } = useTranslation()
  const upload = useUploadFile(project.id)
  const input = useRef<HTMLInputElement>(null)
  const onPick = async (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      try {
        await upload.mutateAsync({ file, taskId })
        toast.success(t('projects.files.uploaded', { name: file.name }))
      } catch (e) {
        toastError(e)
      }
    }
    if (input.current) input.current.value = ''
  }
  return (
    <>
      <input ref={input} type="file" multiple className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void onPick(e.target.files)} />
      <Button size={size} variant="outline" onClick={() => input.current?.click()} disabled={upload.isPending}>
        {upload.isPending ? <Loader2 className="animate-spin" /> : <Upload />}
        {t('projects.files.upload')}
      </Button>
    </>
  )
}

type Props = { project: Project; files: ProjectFile[]; canWrite: boolean; layout?: 'grid' | 'list'; showTask?: boolean }

export function FileList({ project, files, canWrite, layout = 'list', showTask }: Props) {
  const { nameOf } = useStaffLookup()
  const { t } = useTranslation()
  const remove = useDeleteFile(project.id)
  const taskById = new Map(project.tasks.map((x) => [x.id, x]))

  const onDelete = async (f: ProjectFile) => {
    if (!(await confirmModal({ title: t('projects.files.deleteTitle'), description: t('projects.files.deleteBody', { name: f.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(f.id, { onSuccess: () => toast.success(t('projects.files.deleted')), onError: toastError })
  }
  const onDownload = (f: ProjectFile) => void downloadFile(project.id, f.id, f.name).catch(toastError)

  if (files.length === 0) return <p className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">{t('projects.files.empty')}</p>

  return (
    <ul className={layout === 'grid' ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3' : 'divide-y rounded-xl border'}>
      {files.map((f) => {
        const Icon = iconOf(f.mime)
        const task = f.taskId ? taskById.get(f.taskId) : undefined
        return (
          <li key={f.id} className={layout === 'grid' ? 'flex items-center gap-3 rounded-xl border bg-card p-3 shadow-xs transition-shadow hover:shadow-card' : 'flex items-center gap-3 p-3'}>
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Icon className="size-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" title={f.name}>
                {f.name}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {fmtSize(f.size)} · {fmtDate(f.uploadedAt)} · {nameOf(f.uploadedBy)}
                {showTask && task ? (
                  <>
                    {' · '}
                    <Link to={`/projects/${project.id}/tasks/${task.id}`} className="text-primary hover:underline">
                      {task.key}
                    </Link>
                  </>
                ) : null}
              </p>
            </div>
            <Button size="icon" variant="ghost" className="size-8" aria-label={t('projects.files.download', { name: f.name })} onClick={() => onDownload(f)}>
              <Download />
            </Button>
            {canWrite ? (
              <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void onDelete(f)}>
                <Trash2 />
              </Button>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
